import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ChevronDown, ChevronUp, Ban } from "lucide-react";
import { OddPill } from "./OddPill";
import { SG7Badge } from "./SG7Badge";
import type { TechnicalPick, PickType } from "../TechnicalPicksCard";

interface MiniPickCardProps {
  pick: TechnicalPick;
  displayOdds: number | null;
  isEstimated?: boolean;
  animationDelay?: number; // For stagger animation
  className?: string;
}

function getStripeColor(type: PickType, isCombo?: boolean): string {
  if (isCombo) return 'hsl(var(--sg7-stripe-amber))';
  
  switch (type) {
    case 'win_home':
    case 'win_away':
    case 'btts_yes':
    case 'goals_over':
    case 'h1_goal':
    case 'h2_goal':
      return 'hsl(var(--sg7-stripe-gold))';
    case 'combo':
    case 'ht_ft':
    case 'double_chance':
    case 'team_total':
    case 'h1_total':
    case 'h2_total':
      return 'hsl(var(--sg7-stripe-amber))';
    case 'no_bet':
      return 'hsl(var(--sg7-stripe-gray))';
    default:
      return 'hsl(var(--sg7-stripe-gray))';
  }
}

export function MiniPickCard({ 
  pick, 
  displayOdds,
  isEstimated = false,
  animationDelay = 0,
  className 
}: MiniPickCardProps) {
  const { t } = useTranslation('analysis');
  const [isExpanded, setIsExpanded] = useState(false);
  const isNoBet = pick.noBet || pick.type === 'no_bet';
  const stripeColor = getStripeColor(pick.type, pick.isCombo);

  // Check if justification needs expansion
  const needsExpansion = pick.justification && pick.justification.length > 80;

  return (
    <div
      className={cn(
        "relative overflow-hidden",
        "rounded-lg",
        "bg-[hsl(var(--sg7-surface-2))]",
        "border border-[hsl(var(--sg7-stroke-subtle))]",
        // SG7 Premium glow on hover/press
        "sg7-glow-hover",
        "hover:bg-[hsl(var(--sg7-surface-hover))]",
        // Stagger fade-in animation
        "sg7-stagger-fade",
        className
      )}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      {/* Left stripe */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: stripeColor }}
      />

      {/* Content */}
      <div className="pl-4 pr-3 py-3">
        {/* Main row: Label + Odds */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            {isNoBet && <Ban className="h-4 w-4 mt-0.5 text-[hsl(var(--sg7-text-muted))] flex-shrink-0" />}
            <span className={cn(
              "font-bold text-sm line-clamp-2",
              isNoBet 
                ? "text-[hsl(var(--sg7-text-muted))]" 
                : "text-[hsl(var(--sg7-text))]"
            )}>
              {pick.label}
            </span>
            {pick.isCombo && (
              <SG7Badge variant="combo" responsive className="flex-shrink-0 mt-0.5" />
            )}
          </div>

          {/* Odds or No Bet */}
          {isNoBet ? (
            <span className="flex-shrink-0 text-[10px] font-semibold text-[hsl(var(--sg7-text-muted))] uppercase">
              {t('picks.noBet')}
            </span>
          ) : (
            <OddPill odds={displayOdds} size="sm" variant="gold" isEstimated={isEstimated} />
          )}
        </div>

        {/* Justification row */}
        {pick.justification && (
          <div className="mt-2">
            <p className={cn(
              "text-xs text-[hsl(var(--sg7-text-muted))] leading-relaxed",
              !isExpanded && needsExpansion && "line-clamp-1"
            )}>
              {pick.justification}
            </p>

            {needsExpansion && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-1 inline-flex items-center gap-0.5 text-[10px] font-semibold text-[hsl(var(--sg7-gold))] hover:text-[hsl(var(--sg7-gold-glow))]"
              >
                {isExpanded ? (
                  <>
                    {t('picks.less')}
                    <ChevronUp className="h-3 w-3" />
                  </>
                ) : (
                  <>
                    {t('picks.more')}
                    <ChevronDown className="h-3 w-3" />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
